import type { ErrorDefinition, FriendlyError } from './types';

/**
 * Audio-related error definitions for recording, playback, and transcription.
 * User-friendly messages following the app's error handling patterns.
 */
export const AUDIO_ERRORS: Record<string, ErrorDefinition> = {
  permission_denied: {
    title: 'Microphone access needed',
    message: 'We need microphone access to hear your answer.',
    action: 'Please allow microphone access in Settings.',
    retryable: true,
    category: 'validation',
    recoverySuggestions: [
      'Open Settings and allow microphone access',
      'You can also type your answer instead',
    ],
  },

  recording_too_short: {
    title: 'Recording too short',
    message: "We couldn't hear your answer clearly.",
    action: 'Please speak your answer and try again.',
    retryable: true,
    category: 'validation',
    recoverySuggestions: [
      'Speak clearly into the microphone',
      'Hold for at least 1 second',
      'Try moving to a quieter location',
    ],
  },

  recording_failed: {
    title: 'Recording failed',
    message: 'There was a problem with the recording.',
    action: 'Please try again.',
    retryable: true,
    category: 'unknown',
  },

  transcription_failed: {
    title: "Couldn't understand",
    message: 'We had trouble understanding your answer.',
    action: 'Please speak clearly and try again.',
    retryable: true,
    retryAfterSeconds: 1,
    category: 'server',
    recoverySuggestions: [
      'Speak more slowly and clearly',
      'Move to a quieter location',
      'Check your internet connection',
    ],
  },

  audio_playback_failed: {
    title: "Couldn't play audio",
    message: 'There was a problem playing the question.',
    action: 'Check your volume and try again.',
    retryable: true,
    category: 'unknown',
    recoverySuggestions: [
      'Check that your volume is turned up',
      'Make sure your device is not on silent',
      'Try closing other audio apps',
    ],
  },

  audio_not_found: {
    title: 'Audio not available',
    message: "The audio for this question isn't available.",
    action: 'You can read the question instead.',
    retryable: false,
    category: 'unknown',
  },

  grading_failed: {
    title: "Couldn't check answer",
    message: 'We had trouble checking your answer.',
    action: 'Please try again.',
    retryable: true,
    retryAfterSeconds: 1,
    category: 'server',
  },

  low_confidence: {
    title: 'Please review',
    message: "We're not sure we heard you correctly.",
    action: 'Please review your answer and try again if needed.',
    retryable: true,
    category: 'validation',
  },
};

/**
 * Get a friendly error for an audio-related error code
 */
export function getAudioError(code: string): FriendlyError {
  const errorDef = AUDIO_ERRORS[code] ?? {
    title: 'Something went wrong',
    message: 'An unexpected error occurred.',
    action: 'Please try again.',
    retryable: true,
    category: 'unknown' as const,
  };

  return {
    ...errorDef,
    code,
  };
}
